import React, { useMemo } from 'react';
import { FaCalendarAlt, FaChevronRight } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { EVENT_TYPE_COLORS } from '../../constants/notificationTypes';

const YearView = ({ 
  currentYear, 
  eventi = [], 
  onMonthSelect
}) => {
  const { theme } = useTheme();

  console.log('🗓️ YearView - Eventi ricevuti:', {
    totalEvents: eventi.length,
    currentYear,
    firstThreeEvents: eventi.slice(0, 3).map(e => ({
      id: e.CAL_Id_CalendarioEventi,
      title: e.CAL_Titolo,
      date: e.CAL_DataOraInizio,
      type: e.CAL_TipoEvento,
      typeId: e.CAL_Id_TipiEventi, // ✅ NEW: Log dell'ID tipo
      allFields: Object.keys(e)
    }))
  });

  // ✅ FIXED: Debug specifico per i tipi di evento usando CAL_Id_TipiEventi
  console.log('🔍 Debug tipi evento:', {
    eventiWithTypeId: eventi.filter(e => e.CAL_Id_TipiEventi).length,
    eventiWithoutTypeId: eventi.filter(e => !e.CAL_Id_TipiEventi).length,
    uniqueTypeIds: [...new Set(eventi.map(e => e.CAL_Id_TipiEventi).filter(Boolean))],
    sampleEventFields: eventi[0] ? Object.keys(eventi[0]) : [],
    EVENT_TYPE_COLORS_KEYS: Object.keys(EVENT_TYPE_COLORS),
    // ✅ NEW: Mappatura ID -> nome
    typeMappings: [...new Set(eventi.map(e => e.CAL_Id_TipiEventi).filter(Boolean))].map(id => ({
      id,
      found: !!EVENT_TYPE_COLORS[id],
      config: EVENT_TYPE_COLORS[id]
    }))
  });

  // Group events by month
  const eventiPerMese = useMemo(() => {
    console.log('📊 Calcolo eventi per mese...');
    const map = {};
    
    for (let month = 0; month < 12; month++) {
      map[month] = eventi.filter(evento => {
        const eventDate = new Date(evento.CAL_DataOraInizio);
        const isValidDate = !isNaN(eventDate.getTime());
        return isValidDate && 
               eventDate.getFullYear() === currentYear && 
               eventDate.getMonth() === month;
      });
    }
    
    console.log('📊 Eventi per mese calcolati:', Object.fromEntries(
      Object.entries(map).map(([month, events]) => [
        `mese_${parseInt(month) + 1}`, 
        events.length
      ])
    ));
    
    return map;
  }, [eventi, currentYear]);

  // Mini month component with animations
  const MiniMonth = ({ monthIndex }) => {
    const monthNames = [
      'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
      'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
    ];
    
    const monthEvents = eventiPerMese[monthIndex] || [];
    const firstDay = new Date(currentYear, monthIndex, 1);
    const lastDay = new Date(currentYear, monthIndex + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Group events by day
    const eventiPerGiorno = {};
    monthEvents.forEach(evento => {
      const eventDate = new Date(evento.CAL_DataOraInizio);
      if (!isNaN(eventDate.getTime())) {
        const day = eventDate.getDate();
        if (!eventiPerGiorno[day]) eventiPerGiorno[day] = [];
        eventiPerGiorno[day].push(evento);
      }
    });

    // ✅ FIXED: Event type statistics usando CAL_Id_TipiEventi
    const eventStats = monthEvents.reduce((acc, evento) => {
      const tipoId = evento.CAL_Id_TipiEventi; // ✅ FIXED: Usa ID invece di nome
      if (tipoId) {
        acc[tipoId] = (acc[tipoId] || 0) + 1;
      }
      return acc;
    }, {});

    // Theme styles
    const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
    const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
    const headerBg = theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100';
    const cellBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';

    return (
      <div
        className={`
          ${cellBg} border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden
          cursor-pointer transition-all duration-300 ease-in-out
          hover:shadow-lg hover:shadow-blue-500/20 hover:scale-[1.02] hover:border-blue-400
          transform-gpu group
        `}
        onClick={() => onMonthSelect?.(monthIndex)}
      >
        <div className={`
          ${headerBg} p-3 border-b transition-colors duration-300
          group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30
          flex justify-between items-center
        `}>
          <h3 className={`font-bold ${textColor} transition-colors duration-300`}>
            {monthNames[monthIndex]} {currentYear}
          </h3>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${mutedText} transition-colors duration-300`}>
              {monthEvents.length} eventi
            </span>
            <FaChevronRight className={`
              w-3 h-3 ${mutedText} transition-all duration-300
              group-hover:translate-x-1 group-hover:text-blue-500
            `} />
          </div>
        </div>

        {/* Mini calendar grid */}
        <div className="p-2">
          {/* ✅ FIXED: Weekday headers starting from Monday */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((day, index) => (
              <div key={`weekday-${index}`} className={`text-xs text-center ${mutedText}`}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {/* ✅ FIXED: Empty cells for month start (Monday-based) */}
            {Array.from({ length: (firstDay.getDay() + 6) % 7 }, (_, i) => (
              <div key={`empty-${i}`} className="w-6 h-6" />
            ))}
            
            {/* Month days */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dayEvents = eventiPerGiorno[day] || [];
              const isToday = new Date().toDateString() === new Date(currentYear, monthIndex, day).toDateString();
              
              return (
                <div
                  key={day}
                  className={`
                    w-6 h-6 text-xs flex items-center justify-center relative
                    transition-all duration-200 hover:scale-110 cursor-pointer
                    ${isToday ? 'bg-blue-600 text-white rounded-full' : textColor}
                    ${dayEvents.length > 0 ? 'font-bold hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded' : ''}
                  `}
                  title={dayEvents.length > 0 ? `${dayEvents.length} eventi` : ''}
                >
                  {day}
                  {/* Event indicator */}
                  {dayEvents.length > 0 && !isToday && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-0.5 bg-blue-500 rounded-full transition-all duration-200 group-hover:bg-blue-600 group-hover:h-1" />
                  )}
                  {/* Multiple events indicator */}
                  {dayEvents.length > 1 && (
                    <div className={`
                      absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 text-white
                      rounded-full text-[8px] flex items-center justify-center font-bold
                      transition-all duration-200 hover:scale-125
                    `}>
                      {dayEvents.length > 9 ? '9+' : dayEvents.length}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ✅ FIXED: Event type indicators usando CAL_Id_TipiEventi */}
        {Object.keys(eventStats).length > 0 && (
          <div className="p-2 border-t border-gray-200 dark:border-gray-600">
            <div className="flex flex-wrap gap-1">
              {Object.entries(eventStats).slice(0, 3).map(([tipoId, count]) => {
                const typeConfig = EVENT_TYPE_COLORS[tipoId]; // ✅ FIXED: Usa tipoId numerico
                if (!typeConfig) return null;
                
                return (
                  <div
                    key={tipoId}
                    className={`
                      text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1
                      transition-all duration-200 hover:scale-105 cursor-help
                      bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900/30
                    `}
                    title={`${typeConfig.descrizione}: ${count} eventi`}
                  >
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: typeConfig.color }}
                    />
                    <span className={`${textColor} font-medium`}>{count}</span>
                  </div>
                );
              })}
              {Object.keys(eventStats).length > 3 && (
                <div className={`text-xs ${mutedText} px-1`}>
                  +{Object.keys(eventStats).length - 3}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ✅ FIXED: Year statistics usando CAL_Id_TipiEventi
  const yearStats = useMemo(() => {
    console.log('📊 Calcolo statistiche anno per', currentYear, '...');
    console.log('📊 Eventi totali ricevuti:', eventi.length);
    
    const totalEvents = eventi.length;
    
    // ✅ FIXED: Conta eventi per tipo usando CAL_Id_TipiEventi
    const eventsByType = eventi.reduce((acc, evento) => {
      const tipoId = evento.CAL_Id_TipiEventi; // ✅ FIXED: Usa ID numerico
      if (tipoId) {
        acc[tipoId] = (acc[tipoId] || 0) + 1;
      }
      return acc;
    }, {});

    // ✅ FIXED: Debug per controllare la mappatura
    console.log('📊 Eventi per tipo ID:', eventsByType);
    const mappedEventsByType = {};
    Object.entries(eventsByType).forEach(([tipoId, count]) => {
      const typeConfig = EVENT_TYPE_COLORS[tipoId];
      if (typeConfig) {
        mappedEventsByType[typeConfig.descrizione] = count;
      }
    });
    console.log('📊 Eventi per tipo mappati:', mappedEventsByType);

    // ✅ FIXED: Mesi più impegnati con nomi corretti
    const busyMonths = Object.keys(eventiPerMese)
      .map(month => ({ 
        month: parseInt(month), 
        count: eventiPerMese[month].length,
        name: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'][parseInt(month)]
      }))
      .filter(({ count }) => count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    console.log('📊 Statistiche calcolate:', { 
      totalEvents, 
      eventsByType, 
      busyMonths: busyMonths.map(m => `${m.name}: ${m.count}`)
    });

    return { totalEvents, eventsByType, busyMonths };
  }, [eventi, eventiPerMese, currentYear]);

  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const cardBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';

  return (
    <div className="space-y-6">
      {/* Year header and statistics */}
      <div className={`${cardBg} rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FaCalendarAlt className="text-blue-600 text-2xl" />
              <div>
                <h1 className={`text-2xl font-bold ${textColor}`}>
                  Anno {currentYear}
                </h1>
                <p className={`text-sm ${mutedText}`}>
                  {yearStats.totalEvents} eventi totali
                </p>
              </div>
            </div>
            
            {/* ✅ NEW: Quick stats */}
            <div className="text-right">
              <div className={`text-sm ${mutedText}`}>
                {Object.keys(yearStats.eventsByType).length} tipi di eventi
              </div>
              <div className={`text-sm ${mutedText}`}>
                {yearStats.busyMonths.length} mesi attivi
              </div>
            </div>
          </div>

          {/* ✅ FIXED: Statistics con tipi evento corretti */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Event types */}
            <div>
              <h4 className={`font-medium ${textColor} mb-3`}>Eventi per Tipo</h4>
              <div className="space-y-2">
                {Object.keys(yearStats.eventsByType).length === 0 ? (
                  <div>
                    <p className={`text-sm ${mutedText} italic`}>Nessun evento per questo anno</p>
                  </div>
                ) : (
                  Object.entries(yearStats.eventsByType).map(([tipoId, count]) => {
                    const typeConfig = EVENT_TYPE_COLORS[tipoId]; // ✅ FIXED: Usa tipoId numerico
                    if (!typeConfig) return null;
                    
                    const percentage = ((count / yearStats.totalEvents) * 100).toFixed(1);
                    
                    return (
                      <div 
                        key={tipoId} 
                        className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 rounded transition-all duration-200 cursor-pointer"
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: typeConfig.color }}
                            />
                            <span className={`
                              font-medium transition-colors
                              ${theme === 'dark' ? 'text-gray-100 group-hover:text-blue-300' : 'text-gray-900 group-hover:text-blue-600'}
                            `}>
                              {typeConfig.descrizione}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`
                              font-medium transition-colors
                              ${theme === 'dark' ? 'text-gray-100 group-hover:text-blue-300' : 'text-gray-900 group-hover:text-blue-600'}
                            `}>
                              {count} eventi
                            </span>
                            <span className={`text-xs ${mutedText} opacity-0 group-hover:opacity-100 transition-opacity`}>
                              {percentage}%
                            </span>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="mt-1 h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500 group-hover:from-emerald-600 group-hover:to-emerald-700"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Busy months */}
            <div>
              <h4 className={`font-medium ${textColor} mb-3`}>Mesi Più Impegnati</h4>
              <div className="space-y-2">
                {yearStats.busyMonths.length === 0 ? (
                  <p className={`text-sm ${mutedText} italic`}>Nessun evento trovato</p>
                ) : (
                  yearStats.busyMonths.map(({ month, count, name }, index) => {
                    const percentage = ((count / yearStats.totalEvents) * 100).toFixed(1);
                    const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉';
                    
                    return (
                      <div 
                        key={month} 
                        className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 rounded transition-all duration-200 cursor-pointer"
                        onClick={() => onMonthSelect?.(month)}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{medal}</span>
                            <span className={`
                              font-medium transition-colors
                              ${theme === 'dark' ? 'text-gray-100 group-hover:text-blue-300' : 'text-gray-900 group-hover:text-blue-600'}
                            `}>
                              {name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`
                              font-medium transition-colors
                              ${theme === 'dark' ? 'text-gray-100 group-hover:text-blue-300' : 'text-gray-900 group-hover:text-blue-600'}
                            `}>
                              {count} eventi
                            </span>
                            <span className={`text-xs ${mutedText} opacity-0 group-hover:opacity-100 transition-opacity`}>
                              {percentage}%
                            </span>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="mt-1 h-1 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 group-hover:from-blue-600 group-hover:to-blue-700"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ IMPROVED: 12-month grid with better animations */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 12 }, (_, monthIndex) => (
          <MiniMonth key={monthIndex} monthIndex={monthIndex} />
        ))}
      </div>
    </div>
  );
};

export default YearView;